<template>
  <div class="vacancy-responses-card" :class="{ _loading: loading.resumes }">
    <div
      v-if="showNewResponsesBanner"
      class="vacancy-responses-card__new-responses-banner"
    >
      {{ $tMyResponses('There are new responses') }}
    </div>

    <VacancyResponsesCardHeader
      :is-extended-card="show.extension"
      :response-vacancy="responseVacancy"
      @arrow-down-click="toggleExtension"
      @click.native="toggleExtension"
    />

    <CollapseTransition>
      <div
        v-if="show.extension"
        class="vacancy-responses-card__extension"
      >
        <div class="vacancy-responses-card__responses-wrap">
          <div class="vacancy-responses-card__responses-wrap-filters">
            <BaseSelect
              v-if="mdOrDown"
              v-model="filterOptions.sort"
              text-key="label"
              :options="sortTypes"
              @input="loadResumeResponses"
            />
            <template v-else>
              <div
                v-for="filter in responsesFilters"
                :key="filter.title"
              >
                <div>{{ filter.title }}:</div>

                <BaseMenu ref="filterMenus" :card-style="{ transform: 'translate(0.4rem, -3rem)', minWidth: '100%' }">
                  <template v-slot:activator="{ on }">
                    <button class="base-btn base-btn--small vacancy-responses-card__responses-filter-value" v-on="on">
                      {{ filter.value }}
                      <BaseSvg name="ico-arrow" width="0.9rem" height="0.6rem" />
                    </button>
                  </template>

                  <div style="min-width: 16rem">
                    <div class="vacancy-responses-card__responses-filters-menu-title">{{ filter.menu.title }}</div>

                    <div v-if="filter.menu.text" class="mb-4">{{ filter.menu.text }}</div>

                    <div
                      class="account-vacancies-responses__menu-inputs-list"
                      :class="{ '_has-ranges': filter.menu.inputs.ranges }"
                    >
                      <BaseCheckbox
                        v-for="(checkbox, index) in filter.menu.inputs.checkboxes"
                        :key="index"
                        color="#3674ED"
                        :label="checkbox.label"
                        :checked="isCheckedInCheckboxesList(checkbox.list, checkbox.idKey, checkbox)"
                        @input="bindWithCheckboxesList(checkbox.list, checkbox.idKey, $event, checkbox); onCheckboxesInput()"
                      />

                      <BaseRadioList
                        v-if="filter.menu.inputs.radios"
                        v-model="filterOptions.sort"
                        text-key="label"
                        vertical
                        :options="filter.menu.inputs.radios"
                        @input="onFiltersRadiosInput"
                      />

                      <VueSlider
                        v-for="(range, index) in filter.menu.inputs.ranges"
                        :key="index + 1000000"
                        v-model="filterOptions.matching"
                        tooltip="always"
                        tooltip-placement="bottom"
                        :dot-size="12"
                        :interval="10"
                        class="account-vacancies-responses__menu-inputs-slider relative"
                        @change="onRangeSliderInput"
                      />

                      <div class="account-vacancies-responses__menu-inputs-reset-wrap">
                        <button
                          class="base-btn base-btn--outline-dark base-btn--x-small mt-2"
                          @click="resetFilter(filter)"
                        >
                          {{ $tMyResponses('Reset') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </BaseMenu>
              </div>
            </template>
          </div>

          <div class="vacancy-responses-card__responses-table">
            <div class="vacancy-responses-card__responses-table-header">
              <BaseMenu ref="allFiltersMenu" v-if="mdOrDown" :card-style="{ top: '0.4rem' }">
                <template v-slot:activator="{ on }">
                  <VacanciesResponsesFilterSelector
                    :value="$tMyResponses('Candidates filter')"
                    :filters-count="activeFiltersCount"
                    v-on="on"
                    @clear="resetAllFilters"
                  />
                </template>

                <div class="vacancy-responses-card__responses-filters-menu-small">
                  <div
                    v-for="filter in responsesFilters"
                    :key="filter.title"
                  >
                    <div style="min-width: 15rem">
                      <div class="vacancy-responses-card__responses-filters-menu-title">{{ filter.menu.title }}</div>

                      <div v-if="filter.menu.text" class="d-flex mb-4 ml-2">{{ filter.menu.text }}</div>

                      <div class="account-vacancies-responses__menu-inputs-list">
                        <BaseCheckbox
                          v-for="(checkbox, index) in filter.menu.inputs.checkboxes"
                          :key="index"
                          color="#3674ED"
                          :label="checkbox.label"
                          :checked="isCheckedInCheckboxesList(checkbox.list, checkbox.idKey, checkbox)"
                          @input="bindWithCheckboxesList(checkbox.list, checkbox.idKey, $event, checkbox); onCheckboxesInput()"
                        />

                        <BaseRadioList
                          v-if="filter.menu.inputs.radios"
                          v-model="filterOptions.sort"
                          text-key="label"
                          vertical
                          :options="filter.menu.inputs.radios"
                          @input="onFiltersRadiosInput"
                        />

                        <VueSlider
                          v-for="(range, index) in filter.menu.inputs.ranges"
                          :key="index + 1000000"
                          v-model="filterOptions.matching"
                          tooltip="always"
                          tooltip-placement="bottom"
                          :dot-size="12"
                          :interval="10"
                          class="account-vacancies-responses__menu-inputs-slider"
                          @change="onRangeSliderInput"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    class="base-btn base-btn--outline-dark base-btn--x-small"
                    @click="resetAllFilters"
                  >
                    {{ $tMyResponses('Reset') }}
                  </button>
                </div>
              </BaseMenu>
              <template v-else>
<!--          TODO temporary filter (hide matching)      -->
                <div
                  v-for="(column, index) in responsesGridColumns.filter((_, idx) => idx !== 3)"
                  :key="index"
                  :style="column.style"
                  class="account-vacancies-responses__description-text"
                >
                  <span>{{ column.title }}</span>
                </div>
              </template>
            </div>

            <div v-if="loading.resumes" class="relative pt-10 pb-10">
              <BasePreloader />
            </div>
            <template v-else>
              <VacancyOneResponse
                v-for="resume in lazyResumesList"
                :key="resume.sent_resume_id"
                :grid-columns="responsesGridColumns"
                :resume-response="resume"
                :response-vacancy="responseVacancy"
                @open-phone="$emit('open-resume-phone')"
                @resume-tag-create="loadFilterTags"
                @update:resume-response="updateResumeResponse"
                @reload="loadResumeResponses"
              />
            </template>
          </div>
        </div>
      </div>
    </CollapseTransition>

    <div class="vacancy-responses-card__extend-btn-wrap">
      <button
        class="vacancy-responses-card__extend-btn base-btn base-btn--small base-btn--outline-grey"
        :class="{ '_inverted-icon': show.extension }"
        @click="toggleExtension"
      >
        <BaseSvg name="ico-arrow-down" width="0.9rem" height="0.5rem"  />
      </button>
    </div>
  </div>
</template>

<script>
import { CollapseTransition } from 'vue2-transitions';
import { mapActions, mapGetters } from 'vuex';
import windowSizeMixin from '../../mixins/windowSizeMixin';
import VacancyResponsesCardHeader from './VacancyResponsesCardHeader';
import VacancyOneResponse from './VacancyOneResponse';
import VacanciesResponsesFilterSelector from './VacanciesResponsesFilterSelector';
import VueSlider from 'vue-slider-component'

function defaultFilters() {
  return {
    filter: [],
    matching: 0,
    sort: null,
    tags: [],
  };
}

export default {
  name: 'VacancyResponsesCard',

  components: {
    VacancyResponsesCardHeader,
    VacancyOneResponse,
    CollapseTransition,
    VacanciesResponsesFilterSelector,
    VueSlider,
  },

  mixins: [windowSizeMixin],

  props: {
    responseVacancy: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      show: {
        extension: false,
      },
      loading: {
        resumes: false,
      },

      resumesList: [],
      lazyResumesList: [],

      filterOptions: defaultFilters(),

      rangeSliderTimeout: null,
    };
  },

  computed: {
    ...mapGetters('user', [
      'isEmployer',
    ]),

    showNewResponsesBanner() {
      if (this.resumesList.length) {
        return this.resumesList.some(r => r.is_new);
      }
      return this.responseVacancy.has_new;
    },
    responsesFilters() {
      const tagsList = [].concat(this.tagsList)
        .map(t => ({ label: t.name, value: t.id, list: this.filterOptions.tags, idKey: 'value' }));

      const getFiltersSetTitle = (optionsKey, defaultValue) => {
        const length = this.filterOptions[optionsKey].length;
        if (length === 1) return this.filterOptions[optionsKey][0].label;
        if (length > 1) return `${this.filterOptions[optionsKey][0].label} + ${length - 1}`;
        return defaultValue;
      };

      const getTagsFilterTitle = () => {
        const { tags } = this.filterOptions;
        if (!tags.length) return this.$tMyResponses('No filters');
        if (tags.length === 1) return tags[0].label;
        return tags.length;
      };

      const options = [
        {
          optionsKey: 'filter',
          title: this.$tMyResponses('Filters'),
          value: getFiltersSetTitle('filter', this.$tMyResponses('No filters')),
          menu: {
            title: this.$tMyResponses('Filters'),
            inputs: { checkboxes: this.filtersList },
          },
          default: defaultFilters().filter,
        },
        // {
        //   optionsKey: 'matching',
        //   title: this.$tMyResponses('Matching'),
        //   value: this.filterOptions.matching + '%',
        //   menu: {
        //     title: this.$tMyResponses('Job matching'),
        //     text: `${this.$tMyResponses('Resumes count')}: ${this.matchFilterResumesCount}`,
        //     inputs: { ranges: [{ value: this.filterOptions.matching }] },
        //   },
        //   default: defaultFilters().matching,
        // },
        {
          optionsKey: 'tags',
          title: this.$tMyResponses('tag'),
          value: getTagsFilterTitle(),
          menu: {
            title: this.$tMyResponses('tag'),
            inputs: { checkboxes: tagsList },
          },
          default: defaultFilters().tags,
        },
        {
          optionsKey: 'sort',
          title: this.$tMyResponses('Sorting'),
          value: this.filterOptions.sort.label,
          menu: {
            title: this.$tMyResponses('Sorting'),
            inputs: { radios: this.sortTypes },
          },
          default: defaultFilters().sort,
        },
      ];
      if (this.mdOrDown) options.pop();

      // TODO temporary hide tags for not employers
      if (!this.isEmployer) {
        const rmIdx = options.findIndex(o => o.optionsKey === 'tags');
        options.splice(rmIdx, 1);
      }

      return options;
    },
    activeFiltersCount() {
      const { mark, filter, tags, matching } = this.filterOptions;
      return (mark?.length || 0) + filter.length + tags.length + (matching ? 1 : 0);
    },
    filtersList() {
      return [
        { label: this.$tMyResponses('Only with passport'), value: 'only_with_passport' },
        { label: this.$tMyResponses('Do not show disapproved'), value: 'not_show_declined' },
      ].map(f => ({ ...f, list: this.filterOptions.filter, idKey: 'value' }));
    },
    sortTypes() {
      return [
        { label: this.$tMyResponses('By date of publication'), value: 'created_at' },
        { label: this.$tMyResponses('By conformity'), value: 'matching' },
      ];
    },
    tagsList() {
      return this.responseVacancy.tags || [];
    },
    responsesGridColumns() {
      const columns = [
        {
          title: this.$tMyResponses('Candidate'),
          style: { width: '29%', get minWidth() { return this.width } },
        },
        {
          title: this.$tMyResponses('Phone'),
          style: { width: '15%', get minWidth() { return this.width } },
        },
        {
          title: this.$tMyResponses('Completeness of resume'),
          style: { width: '15%', get maxWidth() { return this.width } },
        },
        {
          title: this.$tMyResponses('Job matching'),
          style: { width: '10%' },
        },
        {
          title: this.$tMyResponses('Note'),
          style: { width: '28%' },
        },
        {
          title: this.$tMyResponses('Actions'),
          style: { width: '3%', minWidth: '4.3rem', marginLeft: 'auto' },
        },
      ];

      return this.mdOrDown ? columns.map(({ title }) => ({ title })) : columns;
    },
    matchFilterResumesCount() {
      return this.responseVacancy.for_match_filter[this.filterOptions.matching];
    },
  },

  methods: {
    ...mapActions('vacancies/responses', [
      'getResponseResumes',
    ]),
    ...mapActions('vacancies', [
      'getVacancyTags',
    ]),

    bindWithCheckboxesList(list, idKey, checked, value) {
      if (checked) list.push(value);
      else {
        const idx = list.findIndex(el => el[idKey] === value[idKey]);
        list.splice(idx, 1);
      }
    },
    isCheckedInCheckboxesList(list, key, value) {
      return !!list.find(el => el[key] === value[key]);
    },

    async toggleExtension() {
      if (this.loading.resumes) return;

      if (this.show.extension) {
        return this.show.extension = false;
      }

      this.show.extension = true;
      this.lazyRenderResumeResponses();

      if (!this.resumesList.length) {
        this.setDefaultOptionsValues();
        await this.loadResumeResponses();
      }
    },
    lazyRenderResumeResponses() {
      const resumesPartCount = 10;
      this.lazyResumesList = this.resumesList.slice(0, resumesPartCount);

      const interval = setInterval(() => {
        this.lazyResumesList = this.lazyResumesList
          .concat(this.resumesList.slice(this.lazyResumesList.length, this.lazyResumesList.length + resumesPartCount));

        if (this.lazyResumesList.length === this.resumesList.length) {
          clearInterval(interval);
        }
      }, 600);
    },
    async loadResumeResponses() {
      this.loading.resumes = true;

      const filters = this.prepareFilters();

      this.resumesList = await this.getResponseResumes({
        vacancy_id: this.responseVacancy.vacancy.ID,
        ...filters,
      });
      this.lazyRenderResumeResponses();

      this.loading.resumes = false;
    },
    prepareFilters() {
      const matching_from = this.filterOptions.matching;
      const filter = this.filterOptions.filter.map(m => m.value);
      const tags = this.filterOptions.tags.map(m => m.value);

      return {
        filter,
        matching_from,
        matching_to: 100,
        sort: this.filterOptions.sort.value,
        tags,
      };
    },
    resetFilter(filter) {
      this.filterOptions[filter.optionsKey] = filter.default;
      this.setDefaultOptionsValues();
      this.loadResumeResponses();
      this.$refs.filterMenus.forEach(menu => menu.close());
    },
    resetAllFilters() {
      this.filterOptions = defaultFilters();
      this.setDefaultOptionsValues();
      this.loadResumeResponses();
      this.$refs.allFiltersMenu.close();
    },
    setDefaultOptionsValues() {
      if (!this.filterOptions.sort) {
        this.filterOptions.sort = this.sortTypes[0];
      }
    },
    async loadFilterTags() {
      const tags = await this.getVacancyTags({ vacancy_id: this.responseVacancy.vacancy.ID });
      this.$set(this.responseVacancy, 'tags', tags);
    },

    updateResumeResponse(resumeResponse) {
      const listResumeResponse = this.resumesList.find(el => el.sent_resume_id === resumeResponse.sent_resume_id);
      Object.assign(listResumeResponse, resumeResponse);
    },
    onCheckboxesInput() {
      this.loadResumeResponses();
    },
    onFiltersRadiosInput() {
      this.loadResumeResponses();
    },
    onRangeSliderInput() {
      if (this.rangeSliderTimeout) clearTimeout(this.rangeSliderTimeout);

      this.rangeSliderTimeout = setTimeout(() => {
        this.loadResumeResponses();
        this.rangeSliderTimeout = null;
      }, 500);
    },
  },
};
</script>
